import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ChartTickService } from '../services/chart-tick.service';
import { FrameCoalescer } from '../charts/frame-coalescer';
import { MAX_AUTO_TICKS } from '../services/axis-scaling';
import { GRIDLINE_STYLE } from '../services/chart-theme';
import { ZoomLatch } from '../services/zoom-latch';
import {
  CURING_BAND_COLOR,
  HEAT_INPUT_BAND_COLORS,
  SERIES_COLORS,
  SERIES_NAMES,
  SERIES_THICKNESS,
  bandFillFor,
  bandInk,
  currentTemperatureStroke,
} from '../services/chart-theme';
import {
  HeatInputStageResolved,
  curingSeriesName,
  heatInputSeriesName,
  heatInputStages,
  heatInputThresholdSlots,
  looksTruncated,
} from '../services/heat-input-stages';
import {
  CuringBandHatchComponent,
  HatchedBand,
} from './curing-band-hatch.component';
import {
  loadSciChart,
  BoxAnnotation,
  EAutoRange,
  EAxisAlignment,
  ECoordinateMode,
  ELabelPlacement,
  ELegendOrientation,
  ENumericFormat,
  FastLineRenderableSeries,
  FastMountainRenderableSeries,
  GradientParams,
  HeatmapColorMap,
  HorizontalLineAnnotation,
  LegendModifier,
  NumberRange,
  NumericAxis,
  Point,
  RolloverModifier,
  RubberBandXyZoomModifier,
  SciChartSurface,
  TSciChart,
  UniformHeatmapDataSeries,
  UniformHeatmapRenderableSeries,
  VerticalLineAnnotation,
  XyDataSeries,
  ZoomPanModifier,
  zeroArray2D,
} from '../charts/scichart';
import { TemperatureDataRow } from '../models/temperature-data-row';
import { ProjectService } from '../services/project.service';
import {
  TelemetryRingBuffer,
} from '../services/telemetry-ring-buffer';
import {
  CuringAccumulators,
  CuringThresholds,
  CURING_TICK_RETENTION,
  DISABLED_THRESHOLD,
} from '../services/curing-accumulators';
import { ChartCaptureService } from '../services/chart-capture.service';
import { AdaptiveResolutionService, ringBufferHandle } from '../services/adaptive-resolution.service';
import {
  ReadonlyTelemetryBuffer,
  TelemetryStoreService,
} from '../services/telemetry-store.service';
import { rowOnLinerGrid } from '../services/telemetry-downsample';
import { ZoneDataRow } from '../models/zone-data-row';
import { Subscription, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { createLeakFreeLegendModifier } from '../charts/legend-without-leak';
import { ProjectTemperatureData } from '../models/project-temperature-data';
import { MarkerInfo } from '../models/marker-info';
import { markerColor } from '../services/marker-color';
import {
  metresToDisplay,
  displayToMetres,
  isNoReading,
  newestReadableIndex,
} from '../services/telemetry-conversion';
import { LossDataRow } from '../models/loss-data-row';
import { ProjectLossData } from '../models/project-loss-data';
import {
  dedupeRealFaults,
  faultSeverityColor,
} from '../health/fault-health.util';
import { closeSubscription, runTeardown } from '../services/safe-teardown';

/**
 * PAINT ORDER FOR THE SEVEN SERIES, as explicit render orders.
 *
 * ORDER IS LOAD-BEARING, not decorative — chart-theme.ts says why: "a cooler
 * threshold is met for longer, so its mountain encloses every hotter one". The
 * five bands are nested filled mountains on ONE shared yAxisId at 32% alpha, so
 * this is genuine occlusion: which series composites over which in screen space.
 *
 * WHY A TABLE AND NOT COLLECTION ORDER. Until 5.2.69 the only lever was position
 * in `renderableSeries`, so updateCuringData removed and re-added three series on
 * EVERY TICK to force them above the bands. 3.1.348 has no render-order API
 * anywhere in the package, so that was not a hack — it was the only mechanism.
 * 5.2.69 adds setRenderOrder/setRenderLayer/setRenderNextTo, and
 * `RenderPassInfo.js:45` positions each renderable by `getRenderOrder() ??
 * items.length`. So appearance stops depending on collection position and the
 * per-tick reshuffle disappears rather than being rewritten.
 *
 * MEASURED, and this is the reason it was worth doing: the reshuffle churned
 * `renderableSeries` every tick, and every churn marks the native legend dirty
 * (SciChartSurface.js:269 -> :1659 -> LegendModifier.onAttachSeries). The legend's
 * rebuild re-subscribes to each series' `isVisibleChanged` and never unsubscribes
 * — `removeEventListenerFromSeries` drops the stored `delete()` that would have.
 * Against a no-churn control that stayed flat at 7, twelve churn cycles took the
 * subscriber count 7 -> 91: exactly +7 per tick, linear, unbounded.
 *
 * The heap cost of the reshuffle ITSELF was separately measured at 0.00 MB /
 * 0.005 ms per tick, so this is not a memory win on the series — the win is that
 * the legend stops accumulating subscribers for the life of a cure.
 */
const CURING_RENDER_ORDER = {
  /** Heat-input bands, coolest first so each nests inside the one below. */
  heatBandBase: 0,
  /** The curing-temperature band sits above all four heat-input bands. */
  curingBand: 10,
  /** Both traces paint above every fill, so a line is never buried. */
  currentTemperature: 11,
  maximalTemperature: 12,
} as const;

@Component({
  selector: 'app-curing',
  templateUrl: './curing.component.html',
  styleUrls: ['./curing.component.scss'],
})
export class CuringComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
   * CHART CONTAINER IDS ARE PER-INSTANCE, NOT HARDCODED.
   *
   * SciChartSurface.create() resolves its host with document.getElementById,
   * which returns the FIRST match in document order. The export prerenders a
   * SECOND copy of this component offscreen; while both copies rendered the same
   * literal id, that lookup handed the offscreen surface the MOUNTED div and
   * creating it tore down the live surface already attached there.
   *
   * The capture NAME is unchanged and is still what ChartCaptureService keys on;
   * only the DOM id is made unique.
   */
  private static nextInstanceId = 0;
  private readonly instanceSuffix = `-${++CuringComponent.nextInstanceId}`;
  readonly curingGraphId = `curing-graph${this.instanceSuffix}`;



  /**
   * True once the SciChart chunk has loaded AND the surfaces exist.
   *
   * The chart container itself is deliberately NOT *ngIf'd on this:
   * SciChartSurface.create() looks the div up by id, so it has to be in the DOM
   * before creation, not after. The flag gates a loading overlay instead, which
   * is what a user should see while a 233 kB chunk is on the wire.
   */
  chartsReady = false;

  errorMessage = '';
  tempDataLoaded: boolean = false;

  // Temp Chart
  /** Bounded row storage — see TelemetryRingBuffer. Indexed by ABSOLUTE tick. */
  /**
   * SHARED temperature rows, owned by TelemetryStoreService.
   *
   * This tab wants a SHORTER window than the others — CURING_TICK_RETENTION
   * against DEFAULT_TICK_RETENTION — and the shared buffer holds the max of what
   * consumers declare, so it retains more than curing alone did. That is the
   * intended trade: one 600-row buffer instead of 600 + 600 + 200, with this tab
   * rendering its own window over it.
   */
  get temperatureDataRows(): ReadonlyTelemetryBuffer<TemperatureDataRow> {
    return this.telemetryStore.read<TemperatureDataRow>('temperature');
  }
  /**
   * Incremental cumulative series (max temp, time-above-curing, time-above-X for
   * up to four heat inputs). Replaces six ticks x width matrices that were
   * rebuilt in full on every poll.
   */
  curingAcc = new CuringAccumulators(CURING_TICK_RETENTION);

  /**
   * Curing re-fits far less than the others — its data-handler zoomExtents calls
   * are already commented out — so the latch here only has to stop applyLinerXRange
   * re-pinning X under a zoom. The Y axes are AutoRange=Always and therefore not
   * zoomable, which viscom confirms matches the desktop rather than being a gap.
   */
  private zoomLatch = new ZoomLatch();
  private programmaticRangeChange = false;
  private dblClickBound = false;

  /** True once the curing chart has rendered real data at least once. */
  private curingBaselineDrawn = false;
  /** Latches the one baseline fetch, so a short backfill cannot restart the cycle. */
  private curingBaselineRequested = false;
  /**
   * The latch may not arm until the chart is built.
   *
   * Adding an axis, and SciChart's default autoRange firing when a series first
   * receives data, both emit visibleRangeChanged — with the programmatic flag
   * clear, because neither is us setting a range. Subscribing before
   * `xAxes.add` therefore armed the latch during construction and suspended
   * every automatic fit for the lifetime of the chart, leaving the axes on their
   * 0-10 defaults. /temperature escaped only because it happens to subscribe
   * after the add.
   *
   * Ordering alone is too subtle to rely on, so this is the belt: a range change
   * is only a user gesture once construction has settled on a later macrotask.
   */
  private chartInteractive = false;

  private armZoomLatch(): void {
    if (!this.chartInteractive) {
      return;
    }
    this.zoomLatch.noteRangeChange(this.programmaticRangeChange);
  }


  /** The right-hand cumulative-duration axis, retained so it can be floored. */
  private timeDurationAxis?: NumericAxis;

  /** The curing x axis, retained so the liner range can be applied to it. */
  private curingXAxis?: NumericAxis;
  /**
   * Liner extent the x axis is currently pinned to, or null if never pinned.
   *
   * Was a boolean, which made the pin once-per-COMPONENT: the FIRST payload to
   * arrive won the axis for the life of the page. `lastSelectedProject` makes the
   * restore load the PREVIOUS project first, so its geometry pinned the axis and
   * the project the user actually picked could never re-pin — a short liner under
   * a long liner's range, stable and silent, surviving a full reload because the
   * restore replays the same order every time.
   *
   * Found on /temperature and fixed there first; this is the same guard, copied.
   * /graph had the same failure through a different mechanism (autoRange: Once
   * framing to the first heatmap it ever saw). Three instances of one pattern:
   * first-data-wins state that nothing re-opens for a new liner.
   *
   * Holding the value keeps the original intent — a repeat payload for the same
   * liner still cannot undo a zoom — while letting a different liner re-frame.
   */
  private appliedLinerEnd: number | null = null;

  /**
   * Pin x to the LINER, exactly as the temperature chart does.
   *
   * Without it the axis auto-ranges Once against an empty series and stops at
   * the 0-10 default, so data spanning 0-68 m renders off-screen entirely.
   */
  private applyLinerXRange(): void {
    if (this.zoomLatch.isZoomed) {
      return;
    }
    const axis = this.curingXAxis;
    const linerEnd = (this.tempData?.CRE ?? 0) - (this.tempData?.CRB ?? 0);
    if (!axis || !(linerEnd > 0)) {
      return;
    }
    // ONCE PER LINER, not once per component — see the note on appliedLinerEnd.
    if (
      this.appliedLinerEnd !== null &&
      Math.abs(this.appliedLinerEnd - linerEnd) < 1e-6
    ) {
      return;
    }
    this.appliedLinerEnd = linerEnd;
    this.programmaticRangeChange = true;
    try {
      axis.autoRange = EAutoRange.Never;
      axis.visibleRange = new NumberRange(0, linerEnd);
    } finally {
      setTimeout(() => (this.programmaticRangeChange = false), 0);
    }
  }

  /**
   * Newest row epoch already ingested, so a history reload arriving on the RT
   * path is recognised as a repeat rather than appended as fresh ticks.
   */
  private lastIngestedUT = 0;
  xyTempDataSeries: XyDataSeries = {} as XyDataSeries;
  xyMaxTempDataSeries: XyDataSeries = {} as XyDataSeries;
  tempOverXDataSeries: XyDataSeries = {} as XyDataSeries;
  tempOverX1DataSeries: XyDataSeries = {} as XyDataSeries;
  tempOverX2DataSeries: XyDataSeries = {} as XyDataSeries;
  tempOverX3DataSeries: XyDataSeries = {} as XyDataSeries;
  tempOverX4DataSeries: XyDataSeries = {} as XyDataSeries;
  tempOverXDataSeriesCollection: XyDataSeries[] = [];

  /** The four heat-input mountains, in the fixed slots X1..X4 (coolest first). */
  heatBandSeries: FastMountainRenderableSeries[] = [];

  /**
   * The stages currently drawn, ascending by temperature. Empty is a real and
   * common state — see updateCuringData.
   */
  activeStages: HeatInputStageResolved[] = [];

  /**
   * True when the project declares no heat-input stages at all. Rendered as
   * "none configured" rather than as an absence the viewer has to interpret: a
   * chart with no bands and no explanation looks like a chart that failed.
   */
  noStagesConfigured = false;

  /**
   * True once the user has dragged the what-if line and released it.
   *
   * Only ever set by commitWhatIfThreshold() and cleared on a project change.
   * Its whole job is to stop the backfill the what-if ITSELF requested from
   * resetting the threshold back to the project's CT — see the temperatureData
   * subscription.
   */
  /**
   * The bands the hatch overlay should paint, remembered so the attach can be
   * retried. See attachHatch for why a retry exists at all.
   */
  private hatchBands: HatchedBand[] = [];

  private whatIfAdopted = false;

  /** The hatch overlay; see CuringBandHatchComponent for why it is not a fill. */
  @ViewChild(CuringBandHatchComponent)
  bandHatch?: CuringBandHatchComponent;

  tick: number = 0;
  tempData: ProjectTemperatureData = {} as ProjectTemperatureData;
  sciChartSurface?: SciChartSurface; // Store reference to chart surface
  isInitialLoad: boolean = true; // Track if this is the first load

  rolloverModifier?: RolloverModifier;
  zoomPanModifier?: ZoomPanModifier;
  rubberBandZoomModifier?: RubberBandXyZoomModifier;

  // channelrange_start: number = 0;
  // channelrange_end: number = 4;
  // channelrange_span: number = 4;
  // samplingInterval: number = 1;

  tempAboveXDataCollection: number[][][] = [];
  tempAbovePoint: number = 70;
  isRunning: boolean = false;

  rangeSliderLeftVal: number = 0;
  rangeSliderRightVal: number = 100;
  showRangeSlider: boolean = false;

  // Chart Controls Events
  public onZoomOut?: () => void;
  public onAddMarker?: () => void;
  public onTempAbovePointChanged?: (temp: number) => void;
  /**
   * The range slider, coalesced to ONE redraw per animation frame.
   *
   * `<input type=range>` fires input several times per animation frame during a
   * drag, and this handler ran a full synchronous redraw for every one of them. The
   * ~75 ms figure behind the /graph tab kill was MEASURED ON THIS SCREEN, and this
   * screen is where the app lands, so the exposure here was the larger one.
   *
   * Same FrameCoalescer /graph uses: trailing edge, at most one follow-up
   * outstanding, superseded values DISCARDED rather than queued. A rate limiter
   * would still eventually perform every event and drift further behind the longer
   * the drag continues.
   */
  private readonly rangeFrames = new FrameCoalescer<[number, number]>(
    ([l, r]) => this.onRangeSliderChange?.(l, r)
  );

  /**
   * The tick a fresh backfill should render at.
   *
   * NOT ZERO. The historical path hardcoded tick 0, so on a completed cure /curing
   * rendered the FIRST sample of the liner — ambient. Measured on :4200: Current and
   * Maximal temperature both 15.75 C (they agree at tick 0 because the running max
   * IS the first value), every "Time over" reading 0:00:00, and the right-hand time
   * axis pinned at zero. Desktop on the same project shows Maximal 75.9 C and
   * 03:51:44 above 70 C.
   *
   * That made the whole screen look broken, and it made the heat-input bands
   * impossible to demonstrate: time-above-threshold at tick 0 is zero BY DEFINITION,
   * so all four bands would render flat whatever else was fixed.
   *
   * The end of the cure is the answer for a historical project — it is what desktop
   * shows and it is the accumulated result the user came for. A live cure reaches the
   * same place via the RT path, which already renders the newest row.
   */
  private latestTick(): number {
    // The newest tick that MEASURED something, not simply the newest.
    //
    // The last rows of a finished cure are all -273.15 C, so the literal newest
    // frame has no current-temperature reading and the chart draws its running
    // max alone — a chart missing a line. Completing the c676653 ruling: show
    // the cure, not its edges.
    const rows = this.temperatureDataRows;
    // A buffer that cannot be READ falls back to the newest tick, which is the
    // behaviour this replaces. tick-follow.spec.ts drives latestTick through a
    // minimal stub with no get(), and a hard call there threw rather than
    // degrading — the arithmetic it guards is not about row contents.
    const at =
      typeof rows?.get === 'function'
        ? (i: number) => rows.get(i) as { TR?: ArrayLike<number> | null } | undefined
        : () => undefined;
    return newestReadableIndex(rows.firstIndex, rows.nextIndex, at);
  }


  public onRangeSliderChange?: (left: number, right: number) => void;

  selectedProjectSubscription?: Subscription;
  tempDataSubscription?: Subscription;
  tempRTDataSubscription?: Subscription;
  lossDataSubscription?: Subscription;
  lossRTDataSubscription?: Subscription;
  tickSubscription?: Subscription;
  vert_annot_positionsSubscription?: Subscription;

  ann_t2: HorizontalLineAnnotation = {} as HorizontalLineAnnotation;

  // Fault severity bands (Desktop Curing.xaml HorizontalFiberFaultAnnotation parity)
  lossData: ProjectLossData = {} as ProjectLossData;
  /** Bounded loss-row storage — see TelemetryRingBuffer. Absolute tick index. */
  /**
   * SHARED loss rows, owned by TelemetryStoreService.
   *
   * Declares the SHORTER window (CURING_TICK_RETENTION) like it does for
   * temperature; the shared buffer holds the max of what consumers ask for and
   * this tab renders its own window over it.
   */
  get lossDataRows(): ReadonlyTelemetryBuffer<LossDataRow> {
    return this.telemetryStore.read<LossDataRow>('loss');
  }
  private lossChangeSub?: Subscription;
  faultBandAnnotations: BoxAnnotation[] = [];

  constructor(
    public projectService: ProjectService,
    private chartCapture: ChartCaptureService,
    private adaptive: AdaptiveResolutionService,
    private chartTick: ChartTickService,
    private telemetryStore: TelemetryStoreService
  ) {}


  ngOnInit() {
    // ONE SHARED TICK. Registration IS the wiring — see ChartTickService.
    this.unregisterTick = this.chartTick.register((tick) => this.updateTempChartForTick(String(tick)));
    //console.log('Temp OnInit called');
    // temperature is registered with the adaptive cap by TelemetryStoreService,
    // which owns that buffer now.
    this.telemetryStore.connect('temperature');
    this.telemetryStore.connect('loss');
    this.telemetryStore.requireRetention(
      'temperature',
      'curing',
      CURING_TICK_RETENTION
    );
    this.telemetryStore.requireRetention('loss', 'curing', CURING_TICK_RETENTION);
    // ORDER MATTERS, and it was wrong.
    //
    // temperatureData is a BehaviorSubject, so subscribing fires SYNCHRONOUSLY
    // with the cached payload. Both initSciChart and createTempGraph are async
    // and neither was awaited, so that first emission landed while
    // xyTempDataSeries was still the `{} as XyDataSeries` placeholder. The
    // handler calls .count() on it, which threw, and an exception in an RxJS
    // next-handler TEARS DOWN the subscription — so every later emission was
    // lost and the chart stayed blank for good.
    //
    // /subviews got away with it only by accident: the sibling <app-graph>
    // initialises first and delayed this subscribe past surface creation. Same
    // component, same data, different timing — which is exactly why it rendered
    // there and not here.
    void this.initialise();
    this.projectService.isHelpOverlayVisible = false;
  }

  /** Surface first, then subscribe. */
  private async initialise(): Promise<void> {
    try {
      await this.initSciChart();
      this.chartsReady = true;
      // Axis labels are baked in from isMetricUnit at construction. Without
      // this the cold-start path builds metric axes and then binds imperial
      // data into them — see ProjectService.whenUnitsSettled.
      await this.projectService.whenUnitsSettled();
      await this.createTempGraph();
    } catch (err) {
      console.error('Curing > chart initialisation failed', err);
    }

    // MOVED here from ngOnInit for uniformity with the other three chart
    // screens. The `if (this.sciChartSurface)` guard below made the early
    // subscription safe, but "safe because of a guard inside the handler" is a
    // weaker property than "cannot run early at all", and it is the kind that
    // quietly stops holding when someone adds a second statement to the handler.
    // changed$ is a ReplaySubject(1), so subscribing here loses nothing.
    this.lossChangeSub = this.telemetryStore
      .changed$('loss')
      .subscribe(() => {
        if (this.sciChartSurface) {
          this.updateFaultSeverityBands(this.sciChartSurface);
        }
      });
    this.setupProjectSubscription();
  }

  /** True once the real data series exist — the placeholders have no methods. */
  private seriesReady(): boolean {
    return typeof this.xyTempDataSeries?.count === 'function';
  }

  /** Release the curing data series before the surface that owns them. */
  private disposeCuringDataSeries(): void {
    const all = [
      this.xyTempDataSeries,
      this.xyMaxTempDataSeries,
      this.tempOverXDataSeries,
      this.tempOverX1DataSeries,
      this.tempOverX2DataSeries,
      this.tempOverX3DataSeries,
      this.tempOverX4DataSeries,
      ...this.tempOverXDataSeriesCollection,
    ];
    for (const series of all) {
      if (series && typeof (series as XyDataSeries).delete === 'function') {
        try {
          series.delete();
        } catch {
          // already deleted
        }
      }
    }
    this.tempOverXDataSeriesCollection = [];
  }

  /** Drops this surface out of the shared tick fan-out. */
  private unregisterTick?: () => void;

  /**
   * THE HATCH IS NOT ATTACHED. It is an ornament we invented; the desktop has no
   * such thing.
   *
   * Joel, on the demo build: "the Time Over CT shows a dotted green shading in
   * addition to the solid green shading. I am not sure what the dotted shading
   * represents or if it should even be there... seems like it might be an
   * artifact leftover from unsuccessful shading attempts?" He read it as an
   * artifact, and on the evidence that is the correct reading.
   *
   * WHAT THE DESKTOP ACTUALLY DOES. `docs/desktop-chart-parity-spec.md:610`,
   * citing `GraphViewModel.cs:1509-1520`: the band fill is a
   * `LinearGradientBrush`, alpha 200 -> 100 at 90 degrees, colour from
   * `ColorPicker.GetColorGradient(i)`. The word "hatch" appears ZERO times in
   * that spec's 1195 lines.
   *
   * CuringBandHatchComponent's docstring asserted the opposite as fact — "the
   * desktop draws each band as a filled, HATCHED mountain" — unsourced, written
   * by me, and then built to. That is the worse half of this: an invented
   * behaviour documented as parity is something the next person builds further on.
   * The component's own header now carries the correction.
   *
   * WHY NOT DELETED. Removing the call is reversible in one line if anyone wants
   * the hatch back while C (the real LinearGradientBrush) is outstanding, and the
   * component is a working, tested piece of canvas-over-WebGL machinery worth
   * keeping for when a band fill genuinely needs a pattern.
   *
   * WHAT IS LOST, measured rather than waved at: nothing reads from this overlay.
   * It painted on its own canvas ABOVE the WebGL surface and was never in the
   * PDF — the export composites three canvases and this was a fourth — so the app
   * and the report DISAGREED about it and now agree. The nesting stays legible
   * through each band's full-strength coloured outline and distinct fill, which is
   * what the printed page has always relied on.
   */
  private attachHatch(): void {
    // Intentionally does nothing. See above.
  }

  ngAfterViewInit(): void {
    this.attachHatch();
  }

  /**
   * TEARDOWN CANNOT BE ALLOWED TO ABORT A NAVIGATION.
   *
   * Angular calls this while DEACTIVATING the route, so anything that escapes
   * here fails the navigation and the user simply does not move — which is
   * what "clicking a tab takes me back to temperature" actually was. Worse,
   * the surfaces above are already deleted by then, so the route you are stuck
   * on is holding dead charts. See safe-teardown.ts for the full stack.
   *
   * A leaked timer is a leak. A broken tab strip is the app not working.
   */
  ngOnDestroy(): void {
    runTeardown('CuringComponent.ngOnDestroy', () => {
      this.unregisterTick?.();
      // BEFORE the surface is deleted. The overlay holds a `rendered` subscription
      // on it and reads its axes every frame; a scheduled frame that ran after
      // delete() would reach into freed WASM memory.
      this.bandHatch?.detach();
      // Drop a frame scheduled but not run; it would redraw a deleted surface.
      this.rangeFrames.cancel();
      this.lossChangeSub?.unsubscribe();
      this.telemetryStore.releaseRetention('temperature', 'curing');
      this.telemetryStore.releaseRetention('loss', 'curing');
      this.disposeCuringDataSeries();
      // Dispose of the chart surface to free up resources
      if (this.sciChartSurface) {
        this.chartCapture.unregister('curingGraph', this.sciChartSurface);
        this.sciChartSurface.delete();
      }
    
      closeSubscription('selectedProjectSubscription', this.selectedProjectSubscription);
      closeSubscription('tempDataSubscription', this.tempDataSubscription);
      closeSubscription('tempRTDataSubscription', this.tempRTDataSubscription);
      closeSubscription('tickSubscription', this.tickSubscription);
      closeSubscription('lossDataSubscription', this.lossDataSubscription);
      closeSubscription('lossRTDataSubscription', this.lossRTDataSubscription);
      //this.tickSubscription.unsubscribe();
      closeSubscription('vert_annot_positionsSubscription', this.vert_annot_positionsSubscription);

      this.curingAcc.configure(0, {
        curing: DISABLED_THRESHOLD,
        x1: DISABLED_THRESHOLD,
        x2: DISABLED_THRESHOLD,
        x3: DISABLED_THRESHOLD,
        x4: DISABLED_THRESHOLD,
      });
    });
  }

  // Setup subscription to recreate chart when project changes
  setupProjectSubscription() {
    this.selectedProjectSubscription =
      this.projectService.selectedProject.subscribe(
        (projectName) => {
          if (projectName && projectName.length > 0) {
            console.log('Curing > Project changed to:', projectName);
            console.log('Curing > Current unit setting:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');
            
            // Skip recreation on initial load (graph is already being created in ngOnInit)
            if (this.isInitialLoad) {
              this.isInitialLoad = false;
              console.log('Curing > Skipping recreation on initial load');
              return;
            }
            
            // Recreate the graph with the new project's unit settings
            this.recreateTempGraph();
          }
        },
        (error) => (this.errorMessage = error)
      );
  }

  // Recreate the temperature graph (called when project changes)
  async recreateTempGraph() {
    console.log('Curing > Recreating graph with units:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');

    // A what-if belongs to the project it was asked about. Carrying it into the
    // next project would silently evaluate that cure against a threshold nobody
    // chose for it.
    this.whatIfAdopted = false;
    
    // Dispose of the existing chart if it exists
    if (this.sciChartSurface) {
      console.log('Curing > Disposing existing chart');
      this.bandHatch?.detach();
      this.chartCapture.unregister('curingGraph', this.sciChartSurface);
      this.sciChartSurface.delete();
      this.sciChartSurface = undefined;
    }

    // Clear data
    this.tempAboveXDataCollection = [];

    // Recreate the chart
    await this.createTempGraph();
  }

  async initSciChart() {
    // SciChart is dynamically imported — nothing below exists until this
    // resolves. This is the only place chart objects are constructed, so it is
    // the only place that needs to wait.
    await loadSciChart();
    SciChartSurface.useWasmFromCDN();
    SciChartSurface.setRuntimeLicenseKey(environment.sciChartKey);
  }

  // Curing Graph ===========================================================================

  async createTempGraph() {
    console.log('Curing > Creating graph with units:', this.projectService.isMetricUnit ? 'Metric (m, °C)' : 'Imperial (ft, °F)');
    
    const { sciChartSurface, wasmContext } = await SciChartSurface.create(
      this.curingGraphId
    );

    // Store reference to the chart surface
    this.sciChartSurface = sciChartSurface;
    this.chartCapture.register('curingGraph', sciChartSurface, this.curingGraphId);

    // Create an X,Y Axis and add to the chart
    const xAxis = new NumericAxis(wasmContext, {
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit ? 'm' : 'ft',
      maxAutoTicks: MAX_AUTO_TICKS,
      majorGridLineStyle: GRIDLINE_STYLE.major,
      minorGridLineStyle: GRIDLINE_STYLE.minor,
    });
    // A fresh axis has never been pinned; the memo belongs to the axis, not to
    // the component, so a rebuild does not leave the new one unfitted.
    this.appliedLinerEnd = null;
    this.curingXAxis = xAxis;

    const yAxis = new NumericAxis(wasmContext, {
      // Always, not the default Once. Once fires when the surface first renders
      // — with an EMPTY series, before the async data arrives — and locks the
      // range at 0-10 for good. That is why three attached, visible series each
      // holding 689 points rendered off-screen against 30-40 C of real data.
      autoRange: EAutoRange.Always,
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit
        ? String.fromCharCode(176).concat('C')
        : String.fromCharCode(176).concat('F'),
      maxAutoTicks: MAX_AUTO_TICKS,
      majorGridLineStyle: GRIDLINE_STYLE.major,
      minorGridLineStyle: GRIDLINE_STYLE.minor,
    });
    yAxis.axisAlignment = EAxisAlignment.Left;
    yAxis.growBy = new NumberRange(0.2, 0.2);

    const yAxisTimeDuration = new NumericAxis(wasmContext, {
      id: 'YAxis_TimeDuration',
      // Same reason as the temperature axis above.
      autoRange: EAutoRange.Always,
      drawLabels: true,
      axisAlignment: EAxisAlignment.Right,
      growBy: new NumberRange(0, 0.2),
      maxAutoTicks: MAX_AUTO_TICKS,
      majorGridLineStyle: GRIDLINE_STYLE.major,
      minorGridLineStyle: GRIDLINE_STYLE.minor,
    });

    // A DURATION axis, so it needs the duration formatter. It was using
    // formatUnixDateToHumanStringHHMMSS_Custom — a WALL-CLOCK formatter — on a
    // cumulative seconds value, which is why small durations rendered as a time
    // of day and near-zero values wrapped to 23:59:59 instead of reading
    // 00:00:00 -> 00:20:21.
    yAxisTimeDuration.labelProvider.formatLabel = (value: number) =>
      this.projectService.formatDurationInSecToHHMMSS_Custom(value);

    sciChartSurface.xAxes.add(xAxis);
    xAxis.visibleRangeChanged.subscribe(() => this.armZoomLatch());
    setTimeout(() => (this.chartInteractive = true), 0);
    sciChartSurface.yAxes.add(yAxis);
    // A floor for the degenerate case. With ambient data nothing crosses the
    // curing threshold, so every time-over value is 0 and autoRange fits to
    // 0..0 — an axis with no extent, which is what produced the sub-zero ticks.
    // One minute of headroom reads as "nothing yet" instead of as a fault, and
    // autoRange Always still takes over the moment a real value appears.
    yAxisTimeDuration.visibleRange = new NumberRange(0, 60);
    this.timeDurationAxis = yAxisTimeDuration;

    sciChartSurface.yAxes.add(yAxisTimeDuration);

    // THE WHAT-IF LINE, and the badge that rides the temperature axis with it.
    //
    // THE BADGE (desktop: the green `70.0\u00B0C` chip pinned to the left axis at the
    // line's height). The annotation already had labelPlacement Axis and
    // showLabel, but with no labelValue SciChart formats the value through the
    // AXIS label provider — two decimals and the axis postfix, so the badge read
    // `70.00\u00B0C`. One decimal is the desktop's format and it is the right one
    // here for a reason the legend's plain `70\u00B0C` does not share: this value is
    // being DRAGGED, so the tenth is the digit that is changing.
    //
    // Coloured from the same token as the curing band it belongs to, so the line,
    // its badge and the mountain it describes cannot drift apart.
    this.ann_t2 = new HorizontalLineAnnotation({
      labelPlacement: ELabelPlacement.Axis,
      showLabel: true,
      stroke: CURING_BAND_COLOR,
      strokeThickness: 3,
      y1: this.tempAbovePoint,
      axisLabelFill: CURING_BAND_COLOR,
      axisFontSize: 14,
      isEditable: true,
      // NOT EMPTY ANY MORE, and deliberately cheap. The badge must track the
      // pointer — a label that lags the line it is attached to is worse than no
      // label — but the RECOMPUTE still waits for release; see dragEnded below.
      // Relabelling is a string assignment; refolding the cure is not.
      onDrag: () => this.updateWhatIfLabel(),
    });
    this.updateWhatIfLabel();

    // THE WHAT-IF TRIGGER. This was the gap: the annotation was draggable and
    // labelled, `onDrag` was an empty function, and nothing ever recomputed. That
    // is exactly the report — "it is there, it is moving, but it is not
    // updating."
    //
    // ON RELEASE, NOT DURING THE DRAG, matching desktop: Curing.xaml fires
    // RecalculateCuringCommand from MouseLeftButtonUp, not from the drag. A
    // recompute per pointer move would also refold the whole cure per pixel,
    // which is the same unbounded-work shape that killed the /graph tab.
    this.ann_t2.dragEnded.subscribe(() => this.commitWhatIfThreshold());

    sciChartSurface.annotations.add(this.ann_t2);

    /**
     * ONE STACK, TWO UNITS — the desktop's rollover, which reads
     *
     *   Time over 70\u00B0C: 03:51:44
     *   Time over 48.9\u00B0C | 300min: 03:09:43
     *   ...
     *   Maximal temperature: 75.9\u00B0C
     *
     * with the time series in hh:mm:ss and the temperature series in NN.N, side
     * by side in the same colour-matched stack.
     *
     * THE UNIT IS DECIDED BY THE AXIS, NOT BY THE SERIES NAME. This used to test
     * `tooltipTitle == 'Current temperature'` against two hardcoded strings.
     * With three series that was merely brittle; with seven it is wrong, because
     * every name is now generated — a heat-input entry is named after its own
     * threshold — and there is no fixed string left to compare. The duration
     * series are exactly the ones on the right-hand duration axis, so ask the
     * axis.
     *
     * TITLE AND VALUE ON ONE LINE, because the template returns one string per
     * line and the desktop puts them together. Two lines per series would make a
     * five-band rollover ten rows tall.
     */
    const customTemplate = (seriesInfo: any, tooltipTitle: any) => {
      const onDurationAxis =
        seriesInfo?.renderableSeries?.yAxisId === 'YAxis_TimeDuration';
      const y = Number(seriesInfo?.yValue);
      const label = tooltipTitle ? `${tooltipTitle}: ` : '';

      if (onDurationAxis) {
        // Same formatter as the axis it is read against. A tooltip showing a time
        // of day beside an axis showing a duration would be worse than either
        // being wrong on its own.
        return [
          `${label}${this.projectService.formatDurationInSecToHHMMSS_Custom(
            Math.floor(y)
          )}`,
        ];
      }

      // ONE DECIMAL for a temperature. The axis carries two (labelPrecision 2),
      // which on a rollover reads as a precision the 0.1\u00B0C source data does not
      // have — desktop shows 75.9\u00B0C, not 75.90\u00B0C.
      const value = Number.isFinite(y) ? y.toFixed(1) : seriesInfo?.formattedYValue;
      return [`${label}${value}${this.unitSuffix()}`];
    };
    this.rolloverModifier = new RolloverModifier({
      tooltipDataTemplate: customTemplate,
    });
    sciChartSurface.chartModifiers.add(this.rolloverModifier);

    // Create new modifier instances to avoid "already attached" errors
    this.rubberBandZoomModifier = new RubberBandXyZoomModifier();
    this.rubberBandZoomModifier.isEnabled = false;
    sciChartSurface.chartModifiers.add(this.rubberBandZoomModifier);

    this.zoomPanModifier = new ZoomPanModifier();
    this.zoomPanModifier.isEnabled = false;
    sciChartSurface.chartModifiers.add(this.zoomPanModifier);

    // Create temp line series
    this.xyTempDataSeries = new XyDataSeries(wasmContext);
    this.xyTempDataSeries.dataSeriesName = SERIES_NAMES.currentTemperature;

    const TempLineSeries = new FastLineRenderableSeries(wasmContext, {
      // Resolved at construction so the LEGEND swatch matches the line; a stroke
      // written after the fact leaves the swatch on its original colour.
      stroke: currentTemperatureStroke(),
      strokeThickness: SERIES_THICKNESS,
      dataSeries: this.xyTempDataSeries,
    });
    // Colour-matched like the bands, so the whole stack reads as one legend.
    TempLineSeries.rolloverModifierProps.tooltipColor = '#F3FEF8';
    TempLineSeries.rolloverModifierProps.tooltipTextColor = '#3B3D3C';
    TempLineSeries.rolloverModifierProps.markerColor = currentTemperatureStroke();
    // sciChartSurface.renderableSeries.add(TempLineSeries);

    // Create Max temp line series
    this.xyMaxTempDataSeries = new XyDataSeries(wasmContext);
    this.xyMaxTempDataSeries.dataSeriesName = SERIES_NAMES.maximalTemperature;

    const maxTempLineSeries = new FastLineRenderableSeries(wasmContext, {
      // Was #F71C1C — close to the desktop's red but not it. /temperature draws
      // the same quantity, so both now read the one token.
      stroke: SERIES_COLORS.maximalTemperature,
      strokeThickness: SERIES_THICKNESS,
      dataSeries: this.xyMaxTempDataSeries,
    });
    maxTempLineSeries.rolloverModifierProps.tooltipColor =
      SERIES_COLORS.maximalTemperature;
    maxTempLineSeries.rolloverModifierProps.tooltipTextColor = '#FFEFEC';
    maxTempLineSeries.rolloverModifierProps.markerColor =
      SERIES_COLORS.maximalTemperature;
    // sciChartSurface.renderableSeries.add(maxTempLineSeries);

    sciChartSurface.chartModifiers.add(
      createLeakFreeLegendModifier({
        showCheckboxes: true,
        orientation: ELegendOrientation.Horizontal,
        // TRANSPARENT, because the default is not. Joel: "I am interested in the
        // legend having a transparent background and matching the theme font."
        //
        // `backgroundColor` falls back to the theme's `legendBackgroundBrush`
        // (SciChartLegend.js:129), and SciChartJSDarkTheme sets that to
        // '#1D2C35FF' — fully opaque, and a blue-grey that belongs to no palette
        // of ours. It is written straight into the container's inline
        // `background-color` (SciChartLegendBase.js:430), so overriding it here
        // is the whole fix; there is no stylesheet to fight.
        //
        // THE FONT NEEDS NOTHING AND IS DELIBERATELY NOT SET. The legend module
        // declares no font at all — the labels inherit `--vc-font-body` from
        // `body` (styles.scss:35) and the checkbox is covered by the existing
        // `input { font-family: inherit }` rule (styles.scss:26-31). Setting a
        // font here would pin the legend to a value that stops following the
        // theme, which is the opposite of what was asked for.
        backgroundColor: 'transparent',
      })
    );

    // THE FIVE TIME-OVER BANDS ------------------------------------------------
    //
    // One mountain per threshold, X = position along the liner, Y = accumulated
    // time above that threshold on the right-hand duration axis. The desktop
    // draws seven legend entries here and this chart drew three: the curing band,
    // the current temperature and the maximal temperature. The four heat-input
    // bands were coded but their threshold source — `HMC` — has shipped empty on
    // every response, so all four were removed from the surface on every update.
    // See heatInputStages() for the two spellings and which one is populated.
    //
    // ADDED COOLEST-FIRST, and the curing band after all four. A cooler threshold
    // is met for longer, so its mountain encloses the hotter ones; adding in
    // ascending order puts each shorter band on top of the taller one it sits
    // inside, which is what makes the nesting legible.

    // The curing-temperature band (CT). Named without a dwell suffix — see
    // curingSeriesName: CT carries no required hold, and "| 0min" would state a
    // requirement nobody set.
    this.tempOverXDataSeries = new XyDataSeries(wasmContext);
    this.tempOverXDataSeries.dataSeriesName = curingSeriesName(
      this.tempAbovePoint,
      this.unitSuffix()
    );

    const mountainSeries = new FastMountainRenderableSeries(wasmContext, {
      yAxisId: 'YAxis_TimeDuration',
      dataSeries: this.tempOverXDataSeries,
      stroke: bandInk(CURING_BAND_COLOR),
      strokeThickness: 1,
      zeroLineY: 0.0,
      // opacity multiplies the fill's own alpha; the alpha is carried in the
      // fill so the legend swatch and the mountain agree on one value.
      fill: bandFillFor(CURING_BAND_COLOR),
    });
    this.styleBandTooltip(mountainSeries, CURING_BAND_COLOR);

    // The four heat-input stages. Constructed unconditionally and attached to the
    // surface only when a stage exists for the slot — see updateCuringData.
    this.tempOverX1DataSeries = new XyDataSeries(wasmContext);
    const mountainSeries1 = new FastMountainRenderableSeries(wasmContext, {
      yAxisId: 'YAxis_TimeDuration',
      dataSeries: this.tempOverX1DataSeries,
      stroke: bandInk(HEAT_INPUT_BAND_COLORS[0]),
      strokeThickness: 1,
      zeroLineY: 0.0,
      fill: bandFillFor(HEAT_INPUT_BAND_COLORS[0]),
    });
    this.styleBandTooltip(mountainSeries1, HEAT_INPUT_BAND_COLORS[0]);

    this.tempOverX2DataSeries = new XyDataSeries(wasmContext);
    const mountainSeries2 = new FastMountainRenderableSeries(wasmContext, {
      yAxisId: 'YAxis_TimeDuration',
      dataSeries: this.tempOverX2DataSeries,
      stroke: bandInk(HEAT_INPUT_BAND_COLORS[1]),
      strokeThickness: 1,
      zeroLineY: 0.0,
      fill: bandFillFor(HEAT_INPUT_BAND_COLORS[1]),
    });
    this.styleBandTooltip(mountainSeries2, HEAT_INPUT_BAND_COLORS[1]);

    this.tempOverX3DataSeries = new XyDataSeries(wasmContext);
    const mountainSeries3 = new FastMountainRenderableSeries(wasmContext, {
      yAxisId: 'YAxis_TimeDuration',
      dataSeries: this.tempOverX3DataSeries,
      stroke: bandInk(HEAT_INPUT_BAND_COLORS[2]),
      strokeThickness: 1,
      zeroLineY: 0.0,
      fill: bandFillFor(HEAT_INPUT_BAND_COLORS[2]),
    });
    this.styleBandTooltip(mountainSeries3, HEAT_INPUT_BAND_COLORS[2]);

    this.tempOverX4DataSeries = new XyDataSeries(wasmContext);
    const mountainSeries4 = new FastMountainRenderableSeries(wasmContext, {
      yAxisId: 'YAxis_TimeDuration',
      dataSeries: this.tempOverX4DataSeries,
      stroke: bandInk(HEAT_INPUT_BAND_COLORS[3]),
      strokeThickness: 1,
      zeroLineY: 0.0,
      fill: bandFillFor(HEAT_INPUT_BAND_COLORS[3]),
    });
    this.styleBandTooltip(mountainSeries4, HEAT_INPUT_BAND_COLORS[3]);

    this.heatBandSeries = [
      mountainSeries1,
      mountainSeries2,
      mountainSeries3,
      mountainSeries4,
    ];

    // PAINT ORDER, SET ONCE. See CURING_RENDER_ORDER for why this replaces a
    // per-tick remove/re-add. The bands attach and detach as stages appear, and
    // a render order travels with the object, so a band re-added later still
    // lands in the right place without anyone re-sorting the collection.
    this.heatBandSeries.forEach((series, i) =>
      series.setRenderOrder(CURING_RENDER_ORDER.heatBandBase + i)
    );
    mountainSeries.setRenderOrder(CURING_RENDER_ORDER.curingBand);
    TempLineSeries.setRenderOrder(CURING_RENDER_ORDER.currentTemperature);
    maxTempLineSeries.setRenderOrder(CURING_RENDER_ORDER.maximalTemperature);

    // THE HATCH. SciChart's mountain fill is a colour, not a brush, so the
    // desktop's diagonal hatch is painted by an overlay clipped to each band's
    // silhouette. Registered coolest-first, plus the curing band, so the overlay
    // paints in the same order the surface does.
    this.hatchBands = [
      ...this.heatBandSeries.map((series, i) => ({
        dataSeries: series.dataSeries as XyDataSeries,
        color: HEAT_INPUT_BAND_COLORS[i]!,
        isVisible: () => series.isVisible,
      })),
      {
        dataSeries: this.tempOverXDataSeries,
        color: CURING_BAND_COLOR,
        isVisible: () => mountainSeries.isVisible,
      },
    ];
    this.attachHatch();

    // Data Subscriptions --------------------------------------------------------
    this.selectedProjectSubscription =
      this.projectService.selectedProject.subscribe(
        (data) => {
          // seriesReady, not a truthiness check: the placeholder is an object and
          // therefore truthy, so `&&` never protected anything.
          if (this.seriesReady() && this.xyTempDataSeries.count() > 0) {
            this.xyTempDataSeries.clear();
          }
        },
        (error) => (this.errorMessage = error)
      );

    // Unsubscribe from previous subscriptions if they exist to prevent memory leaks
    if (this.tempDataSubscription && typeof this.tempDataSubscription.unsubscribe === 'function') {
      this.tempDataSubscription.unsubscribe();
    }
    this.tempDataSubscription = this.projectService.temperatureData.subscribe(
      (data) => {
        if (data == null) {
          console.log('Error in Curing Comp: Temp data is null.');
          return;
        } else if (data.TRC == null) {
          console.log('Error in Curing Comp: Temp data > TRC is null.');
          return;
        }

        this.tempData = data;
        // Size retention to the rows we actually got. The fixed 200-tick cap was
        // the engine of the reload storm: a 362-row backfill overflowed it on
        // arrival, evicting tick 0 immediately.
        const curingRowWidth = data.TRC[0]?.TR?.length ?? 0;
        if (curingRowWidth > 0) {
          const retention = this.adaptive.retentionFor(curingRowWidth);
          // The snapshots must span the same ticks as the rows — see resizeTo.
          this.curingAcc.resizeTo(retention);
        }
        this.applyLinerXRange();
        this.lastIngestedUT =
          data.TRC[data.TRC.length - 1]?.UT ?? this.lastIngestedUT;
        // THE WHAT-IF SURVIVES ITS OWN REFOLD.
        //
        // This line used to be unconditional, and it undid the feature it was
        // part of. commitWhatIfThreshold() adopts the dragged threshold and then
        // asks for the history back, because the folds run from tick 0 — and the
        // answer arrives HERE, where `tempAbovePoint = data.CT` reset it to the
        // project's own curing temperature before refreshCuringAccumulators()
        // below read currentThresholds(). So the refold ran at 70 C, the line
        // snapped back to 70 C, and the drag appeared to do nothing: Rob's
        // original report, reproduced by the fix for it.
        //
        // The adopted value is session-local and per-project: adoptWhatIf() sets
        // the latch, and recreateTempGraph() clears it when the project changes,
        // so a new project always starts from its own CT.
        if (!this.whatIfAdopted) {
          this.tempAbovePoint = data.CT;
          this.setWhatIfLine(this.tempAbovePoint);
          this.tempOverXDataSeries.dataSeriesName = curingSeriesName(
            this.tempAbovePoint,
            this.unitSuffix()
          );
        }

        // Rebuild cumulative state from the FULL response (from tick 0) — the
        // ring buffer only retains a window, but these folds must not restart.
        this.refreshCuringAccumulators(data.TRC);

        this.updateCuringData(
          sciChartSurface,
          TempLineSeries,
          maxTempLineSeries,
          mountainSeries,
          mountainSeries1,
          mountainSeries2,
          mountainSeries3,
          mountainSeries4
        );

        // The END of the cure, not tick 0 — see latestTick().
        this.updateTempChartForTick(String(this.latestTick()));
        //sciChartSurface.zoomExtents();
      },
      (error) => (this.errorMessage = error)
    );

    this.tempRTDataSubscription =
      this.projectService.temperatureRTData.subscribe(
        (data) => {
          console.log('Curing comp > temperatureRTData.subscribe');
          if (data == null) {
            console.log('Error in Curing Comp: Temp RT data is null.');
            return;
          } else if (data.TRC == null) {
            console.log('Error in Curing Comp: Temp RT data > TRC is null.');
            return;
          }

          if (data.TRC.length > 0) {
            // Only rows NEWER than what we hold. A history reload answers on
            // this same path carrying the whole cure, and pushing it wholesale
            // grew the tick count by 362 every cycle — 724, 1086, 1448 — while
            // retention stayed pinned, so the slider's numbering drifted away
            // from reality and tick 0 was evicted the moment it arrived.
            const freshRows = data.TRC.filter(
              (r) => typeof r?.UT !== 'number' || r.UT > this.lastIngestedUT
            );
            for (const row of freshRows) {
            }
            if (freshRows.length > 0) {
              this.lastIngestedUT =
                freshRows[freshRows.length - 1]?.UT ?? this.lastIngestedUT;
            }
            // O(newRows x width) — no full recompute of the cumulative series.
            this.foldCuringRows(freshRows);

            console.log(
              'Curing comp > pushed ' +
                freshRows.length +
                ' row(s). Ticks: ' +
                this.temperatureDataRows.totalCount +
                ' retained: ' +
                this.temperatureDataRows.retainedCount
            );

            this.updateCuringData(
              sciChartSurface,
              TempLineSeries,
              maxTempLineSeries,
              mountainSeries,
              mountainSeries1,
              mountainSeries2,
              mountainSeries3,
              mountainSeries4
            );

            var lastPos = this.temperatureDataRows.nextIndex - 1;
            this.updateTempChartForTick(lastPos.toString());

            //sciChartSurface.zoomExtents();
          }
        },
        (error) => (this.errorMessage = error)
      );

    // FOLLOW THE SCRUB SLIDER.
    //
    // This subscription was commented out, so the shared tick slider published and
    // /curing ignored it: the slider moved and the chart did not. Written fresh
    // rather than uncommented — the dead block indexed `temperatureDataRows[0]`,
    // which is now a ReadonlyTelemetryBuffer with no index access, and it computed
    // `visibleRight` from `rangeSliderLeftVal`, a real bug preserved in a comment.
    //
    // Coalesced, because the play timer and a fast drag both outrun the redraw.
    // Tick now arrives through ChartTickService — ONE subscription for every
    // chart surface, so a screen cannot be added without following the slider.

    // this.tickSubscription = this.projectService.tickPosition.subscribe(
    //   (data) => {
    //     this.updateTempChartForTick(data.toString());

    //     sciChartSurface.zoomExtents();
    //     const linerLength = this.tempData.CRE - this.tempData.CRB;
    //     const visibleLeft = Math.round(
    //       (this.rangeSliderLeftVal / 100) * linerLength
    //     );
    //     const visibleRight = Math.round(
    //       (this.rangeSliderLeftVal / 100) * linerLength
    //     );

    //     this.adjustYAxisVisibleRange(yAxis);
    //   },
    //   (error) => (this.errorMessage = error)
    // );

    // Listen to Control Events:
    const curingHost = document.getElementById(this.curingGraphId);
    if (curingHost && !this.dblClickBound) {
      this.dblClickBound = true;
      curingHost.addEventListener('dblclick', () => this.onZoomOut && this.onZoomOut());
    }

    this.onZoomOut = () => {
      this.zoomLatch.reset();
      console.log(this.tempData.CRB + '-' + this.tempData.CRE);
      //sciChartSurface.zoomExtents();

      sciChartSurface.zoomExtents();
      if (this.showRangeSlider) {
        this.toggleRangeSlider();
      }

      this.adjustYAxisVisibleRange(yAxis);
    };

    this.onRangeSliderChange = (left, right) => {
      //console.log(left + '-' + right);
      const linerLength = this.tempData.CRE - this.tempData.CRB;
      const visibleLeft = Math.round((left / 100) * linerLength);
      const visibleRight = Math.round((right / 100) * linerLength);
      xAxis.visibleRange = new NumberRange(visibleLeft, visibleRight);
    };

    this.vert_annot_positionsSubscription =
      this.projectService.vert_annot_positions.subscribe(
        (data) => {
          this.updateVerticalAnnotations(sciChartSurface, data);
        },
        (error) => (this.errorMessage = error)
      );

    // Loss data subscriptions — feed the fault severity bands (Desktop
    // Curing.xaml hosts the same HorizontalFiberFaultAnnotation as Overview).
    // The curing chart is cumulative-over-time, so bands reflect the latest tick.
    if (this.lossDataSubscription && typeof this.lossDataSubscription.unsubscribe === 'function') {
      this.lossDataSubscription.unsubscribe();
    }
    this.lossDataSubscription = this.projectService.lossData.subscribe(
      (data) => {
        if (data == null || data.LRC == null || data.LRC.length == 0) {
          return;
        }
        // Payload scalars only; the bands read the BUFFER, so they redraw in
        // onLossRowsChanged after the store's write rather than racing it.
        this.lossData = data;
      },
      (error) => (this.errorMessage = error)
    );

    if (this.lossRTDataSubscription && typeof this.lossRTDataSubscription.unsubscribe === 'function') {
      this.lossRTDataSubscription.unsubscribe();
    }
    this.lossRTDataSubscription = this.projectService.lossRTData.subscribe(
      (data) => {
        if (data == null || data.LRC == null || data.LRC.length == 0) {
          return;
        }
        // The store appends; the bands redraw in onLossRowsChanged.
      },
      (error) => (this.errorMessage = error)
    );

    this.onAddMarker = () => {
      this.projectService.addMarker();
    };
  }

  formatxAxisTemperatureGraph(dataValue: number) {
    //adjust the value that user sees on the axis from 0 to end of range instead of from startRange to endRange.
    return (dataValue - this.tempData.CRB).toFixed(2) +
      this.projectService.isMetricUnit
      ? 'm'
      : 'ft';
  }

  updateCuringData(
    sciChart: SciChartSurface,
    tempLineSeries: FastLineRenderableSeries,
    maxTempLineSeries: FastLineRenderableSeries,
    mountainSeries: FastMountainRenderableSeries,
    mountainSeries1: FastMountainRenderableSeries,
    mountainSeries2: FastMountainRenderableSeries,
    mountainSeries3: FastMountainRenderableSeries,
    mountainSeries4: FastMountainRenderableSeries
  ) {
    // Compute Max Temperature Series data(temp array) for each Ticks

    const startDataPoint = this.tempData.LSS;
    const endDataPoint = this.tempData.LES;

    // THE STAGES, from whichever spelling the API sent — long `HeatInput` first,
    // short `HMC` as the fallback. Ascending by temperature, capped at four.
    const stages = heatInputStages(this.tempData);
    this.activeStages = stages;
    this.noStagesConfigured = stages.length === 0;

    // `[]` IS A REAL STATE. 59 of 80 desktop projects declare no heat-input
    // stages because none were configured for that cure. The template says so in
    // words; nothing here throws, and no default is invented.
    if (this.noStagesConfigured) {
      console.log('Curing > no heat-input stages configured for this project');
    } else if (looksTruncated(stages, this.projectService.isMetricUnit)) {
      // Reported, never corrected — the fix belongs on the server. The canonical
      // thresholds are 48.88889 / 54.44445 / 60 / 65.55556 C; arriving as whole
      // degrees they are ~0.9 C low in metric while landing on a tidy-looking
      // 120/130/140/150 F in imperial, so the defect hides in the unit the
      // reporter is least likely to be using.
      console.warn(
        'Curing > heat-input thresholds look truncated to whole degrees — ' +
          'the DTO temperature must be float: ' +
          stages.map((st) => st.temperature).join(', ')
      );
    }

    const unit = this.unitSuffix();
    this.heatBandSeries.forEach((series, i) => {
      const stage = stages[i];
      if (stage) {
        (series.dataSeries as XyDataSeries).dataSeriesName = heatInputSeriesName(
          stage.temperature,
          stage.minutes,
          unit
        );
        if (!sciChart.renderableSeries.contains(series)) {
          sciChart.renderableSeries.add(series);
        }
      } else if (sciChart.renderableSeries.contains(series)) {
      // `, false` IS LOAD-BEARING — see the block comment above this method's
      // z-order reshuffle. SciChart 5 deletes the removed series' dataSeries by
      // default; this series is kept in this.heatBandSeries and re-added when a
      // stage reappears, so deleting it empties the band for the rest of the session.
        sciChart.renderableSeries.remove(series, false);
      }
    });

    // THE PER-TICK Z-ORDER RESHUFFLE IS GONE. These three used to be removed and
    // re-added here on every tick so they painted above the heat-input bands,
    // because collection position was the only lever 3.1.348 offered. They now
    // carry explicit render orders set once in createTempGraph — see
    // CURING_RENDER_ORDER — so their appearance no longer depends on where they
    // sit in the collection, and nothing needs to touch it per tick.
    //
    // The `, false` argument that used to be load-bearing here is not deleted
    // knowledge: it still guards the band detach above, where a series is removed
    // and kept for re-use. It is only the ORDERING churn that has gone.
    //
    // They are still ATTACHED here, once, because a surface rebuild or a project
    // change can leave them off the collection; adding an item already present is
    // a no-op, and it does not mark the legend dirty.
    for (const series of [mountainSeries, tempLineSeries, maxTempLineSeries]) {
      if (!sciChart.renderableSeries.contains(series)) {
        sciChart.renderableSeries.add(series);
      }
    }

    // The cumulative series (max temp, time-above-curing, time-above-X1..X4) are
    // folded incrementally by CuringAccumulators — see refreshCuringAccumulators()
    // on the backfill path and foldCuringRows() on the RT path. This method no
    // longer recomputes them, which is what made it O(ticks x width) per poll.
  }

  /**
   * The what-if line has been released: adopt the new threshold and refold.
   *
   * UNITS. `y1` is in whatever the axis is showing, so it is Fahrenheit whenever
   * the user is imperial. Every threshold downstream — currentThresholds(), the
   * accumulators, the reference implementation — is CELSIUS. Converting here is
   * what stops a 158 F line being compared against Celsius readings and counting
   * nothing at all.
   *
   * SNAPPED TO WHOLE DEGREES CELSIUS, matching desktop's YDragStep="1" on the
   * same annotation. The line is then written back at the snapped value so it
   * cannot sit visually between two thresholds it can never represent.
   *
   * SESSION-LOCAL, AND IT WRITES NOTHING. No API call, no preference, no
   * persistence. Desktop's own what-if is destructive — RecalculateCuringTimes
   * clears and rewrites the project's on-disk Curing storage while never saving
   * the line position, so reopening shows what-if numbers under the ORIGINAL
   * temperature. That is a data-integrity bug in shipping desktop and is
   * deliberately NOT reproduced here.
   */
  private commitWhatIfThreshold(): void {
    const shown = this.ann_t2?.y1;
    if (!Number.isFinite(shown)) {
      return;
    }
    const celsius = this.projectService.isMetricUnit
      ? (shown as number)
      : ((shown as number) - 32) * (5 / 9);
    const snapped = Math.round(celsius);

    // A drag that lands back where it started costs nothing. Without this a
    // stray click on the line would invalidate the cache and re-request the
    // whole backfill for no change.
    if (snapped === this.tempAbovePoint) {
      this.setWhatIfLine(snapped);
      return;
    }

    this.tempAbovePoint = snapped;
    this.whatIfAdopted = true;
    this.setWhatIfLine(snapped);
    // The curing band is named after this threshold, so the legend has to move
    // with the line. Without this the band redrew at 65 C under a legend entry
    // still reading 70 C.
    if (this.seriesReady()) {
      this.tempOverXDataSeries.dataSeriesName = curingSeriesName(
        snapped,
        this.unitSuffix()
      );
    }

    // REFOLD FROM TICK 0 VIA THE EXISTING BACKFILL PATH. currentThresholds()
    // already reads tempAbovePoint, and the temperatureData subscription already
    // calls refreshCuringAccumulators(data.TRC) with the FULL row set — so
    // re-requesting the history is the whole recompute, with no second row
    // source to keep in sync and no extra retention.
    //
    // Refolding the retained ring instead would be wrong and quietly so: those
    // folds accumulate from the start of the cure, and replaying only the window
    // UNDER-REPORTS cure time. The comment on refreshCuringAccumulators says so.
    this.projectService.requestHistoryReload(
      `what-if curing threshold ${snapped} C`
    );
  }

  /**
   * Put the what-if line at a Celsius threshold and relabel its axis badge.
   *
   * ONE PLACE sets both. They were set separately and the badge was never set at
   * all, so the line could be moved by the data (a new project's CT) while the
   * label kept whatever the axis provider last formatted.
   */
  private setWhatIfLine(celsius: number): void {
    if (!this.ann_t2 || typeof this.ann_t2.y1 !== 'number') {
      // The placeholder object before the surface exists; y1 is assignable on it
      // but nothing renders, and updateWhatIfLabel would write to nowhere.
      if (this.ann_t2) {
        this.ann_t2.y1 = this.thresholdInDisplayUnits(celsius);
      }
      return;
    }
    this.ann_t2.y1 = this.thresholdInDisplayUnits(celsius);
    this.updateWhatIfLabel();
  }

  /** The badge text: the line's own value, one decimal, in the axis unit. */
  private updateWhatIfLabel(): void {
    const shown = this.ann_t2?.y1;
    if (!Number.isFinite(shown)) {
      return;
    }
    this.ann_t2.labelValue = `${(shown as number).toFixed(1)}${this.unitSuffix()}`;
  }

  /** A Celsius threshold expressed in whatever the axis is currently showing. */
  private thresholdInDisplayUnits(celsius: number): number {
    return this.projectService.isMetricUnit ? celsius : celsius * (9 / 5) + 32;
  }

  /**
   * Thresholds currently in force: the curing temperature plus the four stages.
   *
   * Reads the stages through heatInputStages(), NOT `HMC` directly. `HMC` is the
   * short spelling and has shipped empty on every response; reading it alone is
   * exactly why four of the desktop's seven series never appeared.
   */
  private currentThresholds(): CuringThresholds {
    const [x1, x2, x3, x4] = heatInputThresholdSlots(
      heatInputStages(this.tempData)
    );
    return { curing: this.tempAbovePoint, x1, x2, x3, x4 };
  }

  /** The temperature unit the axis is currently showing, as a bare suffix. */
  private unitSuffix(): string {
    return this.projectService.isMetricUnit ? '\u00B0C' : '\u00B0F';
  }

  /**
   * Colour-match a band's rollover tooltip to the band itself.
   *
   * The desktop's rollover is ONE stack of colour-matched rows mixing units —
   * four durations, a temperature, another duration. SciChart draws one tooltip
   * per series and stacks them, which is the same reading, but only once each
   * carries its series' colour; left at the default they arrive as five identical
   * grey boxes and the stack stops being decodable at a glance.
   */
  private styleBandTooltip(
    series: FastMountainRenderableSeries,
    color: string
  ): void {
    series.rolloverModifierProps.tooltipColor = bandFillFor(color, 0.92);
    series.rolloverModifierProps.tooltipTextColor = '#FFFFFF';
    series.rolloverModifierProps.markerColor = color;
  }

  /**
   * Rebuild the cumulative state from a FULL backfill response.
   *
   * Must be given the complete row set from tick 0 — these folds accumulate from
   * the start of the cure, so replaying only the retained window would restart the
   * accumulation and under-report peak temperature and cure time.
   */
  private refreshCuringAccumulators(rows: readonly TemperatureDataRow[]): void {
    const startDataPoint = this.tempData.LSS;
    const endDataPoint = this.tempData.LES;
    this.curingAcc.rebuildFrom(
      rows,
      startDataPoint,
      endDataPoint - startDataPoint,
      this.currentThresholds()
    );
  }

  /** Fold newly-arrived RT rows. O(newRows x width). */
  private foldCuringRows(rows: readonly TemperatureDataRow[]): void {
    const startDataPoint = this.tempData.LSS;
    for (const row of rows) {
      this.curingAcc.fold(row, startDataPoint);
    }
  }

  updateTempChartForTick(val: string) {
    const sliderVal: number = +val;
    this.tick = sliderVal;

    // The series are NOT cleared here.
    //
    // Clearing first and then hitting a guard below left all seven series empty
    // until a later render succeeded — measured at ~28 s of blank chart, because
    // the bail also asked for a full history reload and that is how long a
    // backfill takes. Clear only once there is something to draw; see below.
    if (this.temperatureDataRows.totalCount == 0) {
      this.tempDataLoaded = false;
      return;
    }

    // Ring buffer: the tick may have been evicted. Ask for the history back
    // rather than rendering a hole or a stale frame.
    // NEVER BLANK, and never ask for a reload just because a tick is missing.
    // That request was the loop's trigger: miss -> reload -> re-ingest -> evict
    // -> miss. Showing the nearest row we still hold keeps the chart up and asks
    // the server for nothing.
    let row = this.temperatureDataRows.get(sliderVal);
    let snap = this.curingAcc.at(sliderVal);
    if (row == null) {
      const fallback = this.temperatureDataRows.nearest(sliderVal);
      if (fallback) {
        row = fallback.row;
        snap = this.curingAcc.at(fallback.index) ?? snap;
      }
    }
    // A missing SNAPSHOT is not missing history. The rows and the snapshots were
    // sized independently, so a tick could hold a row and no snapshot; treating
    // that as evicted history triggered a full backfill, and the blank lasted the
    // whole ~28 s round trip. Take the nearest snapshot and draw.
    if (snap == null) {
      snap = this.curingAcc.nearest(sliderVal);
    }
    if (row == null || snap == null) {
      // TWO different situations wear the same shape here, and conflating them
      // breaks the chart in one direction or the other.
      //
      //  - No baseline yet: the chart has never drawn, so there is no previous
      //    frame to keep and no data to fall back on. This one NEEDS the fetch.
      //    Suppressing it leaves the chart permanently blank.
      //  - A transient per-tick miss after the chart is up: the previous frame is
      //    still on screen and perfectly serviceable. Fetching here is what
      //    produced the blank-reload-blank cycle, because the bail had already
      //    emptied the series and a backfill takes ~28 s.
      //
      // So: ask once for the baseline, never for a transient miss. The request is
      // latched rather than repeated, so a backfill that comes back still short
      // cannot start the cycle again.
      if (!this.curingBaselineDrawn && !this.curingBaselineRequested) {
        this.curingBaselineRequested = true;
        this.projectService.requestHistoryReload(
          `curing baseline (no data yet, tick ${sliderVal})`
        );
      }
      return;
    }

    // Safe to clear now — every append below is guaranteed to follow.
    if (this.tempDataLoaded) {
      this.xyTempDataSeries.clear();
      this.xyMaxTempDataSeries.clear();
      this.tempOverXDataSeries.clear();
      this.tempOverX1DataSeries.clear();
      this.tempOverX2DataSeries.clear();
      this.tempOverX3DataSeries.clear();
      this.tempOverX4DataSeries.clear();
    }
    this.tempDataLoaded = true;
    // The chart has drawn at least once: from here a miss is transient and must
    // not trigger another fetch.
    this.curingBaselineDrawn = true;
    this.curingBaselineRequested = false;

    const startDataPoint = this.tempData.LSS;
    const endDataPoint = this.tempData.LES;

    var xValues_Temp: number[] = new Array(endDataPoint - startDataPoint);
    var yValues_Temp: number[] = new Array(endDataPoint - startDataPoint);

    var xValues_MaxTemp: number[] = new Array(
      snap.max.length
    );
    var yValues_MaxTemp: number[] = new Array(
      snap.max.length
    );

    var xValues_OverX: number[] = new Array(
      snap.aboveCuring.length
    );
    var yValues_OverX: number[] = new Array(
      snap.aboveCuring.length
    );

    var xValues_OverX1: number[] = new Array(
      (snap.above1?.length ?? 0)
    );
    var yValues_OverX1: number[] = new Array(
      (snap.above1?.length ?? 0)
    );

    var xValues_OverX2: number[] = new Array(
      (snap.above2?.length ?? 0)
    );
    var yValues_OverX2: number[] = new Array(
      (snap.above2?.length ?? 0)
    );

    var xValues_OverX3: number[] = new Array(
      (snap.above3?.length ?? 0)
    );
    var yValues_OverX3: number[] = new Array(
      (snap.above3?.length ?? 0)
    );

    var xValues_OverX4: number[] = new Array(
      (snap.above4?.length ?? 0)
    );
    var yValues_OverX4: number[] = new Array(
      (snap.above4?.length ?? 0)
    );

    // Create empty collection of temp over X Array for each Heat Marker
    var yValues_OverXCollection: number[][] = [];

    // for (
    //   let heatMarkerIndex = 0;
    //   heatMarkerIndex < this.tempData.HMC.length;
    //   heatMarkerIndex++
    // ) {
    //   const hmTemp = this.tempData.HMC[heatMarkerIndex];

    //   var yValues_OverX: number[] = new Array(
    //     this.tempAboveXDataCollection[0][sliderVal].length
    //   );

    //   yValues_OverXCollection.push(yValues_OverX);
    // }

    // X Factor = Liner Length / Data Points Count

    const xFactor =
      (this.tempData.CRE - this.tempData.CRB) / (endDataPoint - startDataPoint);
    //console.log(xFactor);
    //console.log('dp start:' + startDataPoint + '  end:' + endDataPoint);

    // Project the row onto the liner grid FIRST. `row.TR[j]` is only the sample
    // at fiber index j when the row is the whole array; a decimated or
    // liner-trimmed row holds a different sample there, and past its end yields
    // undefined — which the `?? 0` below would have quietly plotted as a flat zero
    // trace indistinguishable from real telemetry.
    const linerTemps = rowOnLinerGrid(
      row.TR,
      row.SIX,
      startDataPoint,
      endDataPoint
    );

    // OMIT NO-READING SAMPLES FROM THE TWO TEMPERATURE TRACES, never plot them.
    //
    // MEASURED on S3640: the last FOUR rows of that cure are all -273.15 C — raw
    // ushort zero, what the DTS writes once it stops measuring. The backfill path
    // renders at latestTick(), so a finished cure lands on exactly those rows, and
    // the left axis is autoRange Always. Result, measured in the harness:
    //
    //   tick 295 (latestTick)  y range -354.6 .. 215.7   <- the report's -300 C axis
    //   tick 291 (last real)   y range    5.3 .. 155.7
    //   tick 200 (mid-cure)    y range   16.4 .. 153.8
    //
    // That is page 4 of the cure report: five bands squashed into the top third
    // under labels running down to -300. It is NOT export-specific — any frame
    // sitting on a trailing sentinel row does it — but the export guarantees it,
    // because a freshly prerendered offscreen component always starts at the end.
    //
    // Same rule and the same helper /graph already uses (graph.component.ts:1116):
    // OMIT, NEVER CLAMP. A gap is honest; a plotted absolute zero is a false
    // reading, and the `?? 0` this replaces invented a 0 C reading instead.
    // The bands are unaffected — they are on the duration axis and their folds
    // already ignore anything below threshold.
    let keptTemp = 0;
    let keptMax = 0;

    for (let j = startDataPoint; j < endDataPoint; j++) {
      try {
        const t = linerTemps[j - startDataPoint];
        if (!isNoReading(t)) {
          xValues_Temp[keptTemp] = (j - startDataPoint) * xFactor;
          yValues_Temp[keptTemp] = t as number;
          keptTemp++;
        }
      } catch (error) {
        console.log('error in xyTempDataSeries.append: ' + error + ' - ' + j);
      }

      try {
        const m = snap.max[j - startDataPoint];
        if (!isNoReading(m)) {
          xValues_MaxTemp[keptMax] = (j - startDataPoint) * xFactor;
          yValues_MaxTemp[keptMax] = m as number;
          keptMax++;
        }
      } catch (error) {
        console.log(
          'error in xyMaxTempDataSeries.append: ' + error + ' - ' + j
        );
      }

      try {
        xValues_OverX[j - startDataPoint] = (j - startDataPoint) * xFactor;
        yValues_OverX[j - startDataPoint] =
          snap.aboveCuring[j - startDataPoint] ?? 0;
      } catch (error) {
        console.log(
          'error in tempOverXDataSeries.append: ' + error + ' - ' + j
        );
      }

      try {
        xValues_OverX1[j - startDataPoint] = (j - startDataPoint) * xFactor;
        yValues_OverX1[j - startDataPoint] =
          snap.above1?.[j - startDataPoint] ?? 0;
      } catch (error) {
        console.log(
          'error in tempOverX1DataSeries.append: ' + error + ' - ' + j
        );
      }

      try {
        xValues_OverX2[j - startDataPoint] = (j - startDataPoint) * xFactor;
        yValues_OverX2[j - startDataPoint] =
          snap.above2?.[j - startDataPoint] ?? 0;
      } catch (error) {
        console.log(
          'error in tempOverX2DataSeries.append: ' + error + ' - ' + j
        );
      }

      try {
        xValues_OverX3[j - startDataPoint] = (j - startDataPoint) * xFactor;
        yValues_OverX3[j - startDataPoint] =
          snap.above3?.[j - startDataPoint] ?? 0;
      } catch (error) {
        console.log(
          'error in tempOverX3DataSeries.append: ' + error + ' - ' + j
        );
      }

      try {
        xValues_OverX4[j - startDataPoint] = (j - startDataPoint) * xFactor;
        yValues_OverX4[j - startDataPoint] =
          snap.above4?.[j - startDataPoint] ?? 0;
      } catch (error) {
        console.log(
          'error in tempOverX4DataSeries.append: ' + error + ' - ' + j
        );
      }

      // try {
      //   for (
      //     let heatMarkerIndex = 0;
      //     heatMarkerIndex < this.tempData.HMC.length;
      //     heatMarkerIndex++
      //   ) {
      //     const myValues_OverX = yValues_OverXCollection[heatMarkerIndex];
      //     myValues_OverX[j - startDataPoint] =
      //       this.tempAboveXDataCollection[heatMarkerIndex][sliderVal][
      //         j - startDataPoint
      //       ] ?? 0;
      //   }
      // } catch (error) {
      //   console.log(
      //     'error in tempOverXDataSeries.append: ' + error + ' - ' + j
      //   );
      // }
    }

    // Trim to what survived the sentinel filter; a trailing undefined would be
    // appended as NaN and take the axis with it.
    xValues_Temp.length = keptTemp;
    yValues_Temp.length = keptTemp;
    xValues_MaxTemp.length = keptMax;
    yValues_MaxTemp.length = keptMax;

    this.xyTempDataSeries.appendRange(xValues_Temp, yValues_Temp);
    this.xyMaxTempDataSeries.appendRange(xValues_MaxTemp, yValues_MaxTemp);
    this.tempOverXDataSeries.appendRange(xValues_OverX, yValues_OverX);
    this.tempOverX1DataSeries.appendRange(xValues_OverX1, yValues_OverX1);
    this.tempOverX2DataSeries.appendRange(xValues_OverX2, yValues_OverX2);
    this.tempOverX3DataSeries.appendRange(xValues_OverX3, yValues_OverX3);
    this.tempOverX4DataSeries.appendRange(xValues_OverX4, yValues_OverX4);

    // for (
    //   let heatMarkerIndex = 0;
    //   heatMarkerIndex < this.tempData.HMC.length;
    //   heatMarkerIndex++
    // ) {
    //   const myValues_OverX = yValues_OverXCollection[heatMarkerIndex];
    //   this.tempOverXDataSeriesCollection[heatMarkerIndex].appendRange(
    //     xValues_OverX,
    //     myValues_OverX
    //   );
    // }
  }

  setRollOver(val: boolean) {
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = false;
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = false;
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = val;
  }
  setPan(val: boolean) {
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = false;
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = false;
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = val;
  }
  setZoom(val: boolean) {
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = false;
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = false;
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = val;
  }

  zoomOut() {
    if (!this.onZoomOut) return;
    this.onZoomOut();
  }

  addMarker() {
    if (!this.onAddMarker) return;
    this.onAddMarker();
  }

  /** Delegates to the ONE marker colour resolver; see services/marker-color.ts. */
  convertRgbStringToColor(rgbString: string): string {
    return markerColor(rgbString);
  }

  /**
   * Desktop parity (Curing.xaml hosts the Overview's HorizontalFiberFaultAnnotation):
   * one full-height, severity-coloured band per active severity, from the
   * severity's first fault position to the liner end. Solid low-opacity fill
   * stands in for the Desktop's 45° hatch brush (no hatch in SciChart JS).
   * Uses the latest loss tick — the curing chart is cumulative-over-time.
   */
  updateFaultSeverityBands(sciChart: SciChartSurface) {
    this.faultBandAnnotations.forEach((ann) => {
      if (sciChart.annotations.contains(ann)) {
        sciChart.annotations.remove(ann);
      }
    });
    this.faultBandAnnotations = [];

    if (this.lossDataRows.totalCount === 0 || this.lossData.LES == null) {
      return;
    }

    // Latest tick only — always retained, so no scrub-back miss is possible here.
    const row = this.lossDataRows.last;
    if (!row) {
      return;
    }
    const lss = this.lossData.LSS;
    const les = this.lossData.LES;
    const linerLen = this.lossData.CRE - this.lossData.CRB;
    if (!(les > lss) || !(linerLen > 0)) {
      return;
    }
    const xFactor = linerLen / (les - lss);

    // First (nearest) fault position per active severity at the latest tick.
    const minStartBySeverity = new Map<string, number>();
    dedupeRealFaults(row.FD, row.FP, row.FM).forEach((f) => {
      const existing = minStartBySeverity.get(f.FT);
      if (existing === undefined || f.PS < existing) {
        minStartBySeverity.set(f.FT, f.PS);
      }
    });

    minStartBySeverity.forEach((ps, ft) => {
      const color = faultSeverityColor(ft);
      const xStart = Math.max(0, Math.min((ps - lss) * xFactor, linerLen));
      const band = new BoxAnnotation({
        fill: color,
        stroke: color,
        strokeThickness: 1,
        opacity: 0.3,
        x1: xStart,
        x2: linerLen,
        // Full chart height, independent of the y data range.
        yCoordinateMode: ECoordinateMode.Relative,
        y1: 0,
        y2: 1,
      });
      sciChart.annotations.add(band);
      this.faultBandAnnotations.push(band);
    });
  }

  updateVerticalAnnotations(sciChart: SciChartSurface, markers: MarkerInfo[]) {
    console.log('CuringComponent > updateVerticalAnnotations called with markers:', markers);
    
    // Clear all annotations except ann_t2 and the fault severity bands
    const annotationsToRemove: any[] = [];
    sciChart.annotations.asArray().forEach(ann => {
      if (
        ann !== this.ann_t2 &&
        !this.faultBandAnnotations.includes(ann as BoxAnnotation)
      ) {
        annotationsToRemove.push(ann);
      }
    });
    annotationsToRemove.forEach(ann => sciChart.annotations.remove(ann));
    console.log('CuringComponent > Marker annotations cleared, keeping ann_t2');

    if (!markers || markers.length === 0) {
      console.log('CuringComponent > No markers to add');
      return;
    }

    markers.forEach((mar, index) => {
      // Convert RGB string "r, g, b" to format SciChart understands
      const colorString = this.convertRgbStringToColor(mar.C);
      
      const annt = new VerticalLineAnnotation({
        labelPlacement: ELabelPlacement.Axis,
        showLabel: true,
        stroke: colorString,
        strokeThickness: 3,
        x1: metresToDisplay(mar.P, this.projectService.isMetricUnit),
        axisLabelFill: colorString,
        axisFontSize: 14,
        isEditable: true,
        isSelected: false,
        onDragStarted: () => {
          // Increase thickness during drag for better visibility
          annt.strokeThickness = 6;
        },
        onDragEnded: () => {
          // annt.x1 is in AXIS units; P is persisted in metres.
          const metres = +displayToMetres(
            annt.x1,
            this.projectService.isMetricUnit
          ).toFixed(2);
          // Update the position in the internal array
          mar.P = metres;
          // Persist to API
          this.projectService.updateVerticalAnnotationPosition(index, metres);
          // Reset to normal thickness after drag
          annt.strokeThickness = 3;
        },
      });

      sciChart.annotations.add(annt);
    });
  }

  toggleRangeSlider() {
    this.showRangeSlider = !this.showRangeSlider;

    if (!this.onRangeSliderChange) return;
    this.onRangeSliderChange(0, 100);
  }

  updateChartVisibleRange(leftVal: string, rightVal: string) {
    if (!this.onRangeSliderChange) return;

    if (+leftVal <= +rightVal) {
      this.rangeSliderLeftVal = +leftVal;
      this.rangeSliderRightVal = +rightVal;
    } else {
      this.rangeSliderLeftVal = +rightVal;
      this.rangeSliderRightVal = +leftVal;
    }

    this.rangeFrames.submit([this.rangeSliderLeftVal, this.rangeSliderRightVal]);
  }

  toggleHelpOverlay() {
    this.projectService.isHelpOverlayVisible =
      !this.projectService.isHelpOverlayVisible;
  }

  adjustYAxisVisibleRange(yAxis_Temp: NumericAxis) {
    var visibleMin = 0;
    if (yAxis_Temp.visibleRange.min < -10) {
      visibleMin = -10;
    }
    if (yAxis_Temp.visibleRange.max < this.tempAbovePoint + 10) {
      yAxis_Temp.visibleRange = new NumberRange(
        visibleMin,
        this.tempAbovePoint + 10
      );
    } else if (yAxis_Temp.visibleRange.min < -10) {
      yAxis_Temp.visibleRange = new NumberRange(
        visibleMin,
        yAxis_Temp.visibleRange.max
      );
    }
  }
}
